import React, { useState } from "react";
import PublicLayout from "@/components/PublicLayout";
import SpinnerButton from "@/components/SpinnerButton";
import { publicApi } from "@/lib/api";
import { usePageMeta } from "@/lib/usePageMeta";

export default function Walkin() {
  usePageMeta({ title: "Walk-in Log \u2014 SCW Contact Hub", robots: "noindex, nofollow" });
  const [pin, setPin] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", customer_phone: "", customer_email: "",
    watches_discussed: "", outcome_notes: "",
    served_by: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  // Security Phase 1b (26 Sep 2026). Unlock used to accept any four digits
  // on the client; only submission reached the server. The PIN is now
  // checked by the server at Unlock, and the staff list + today's duty
  // arrive in that same response, so neither is available without the PIN.
  const [staffList, setStaffList] = useState([]);
  const [dutyToday, setDutyToday] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  const unlock = async (e) => {
    e.preventDefault();
    if (pin.length !== 4 || unlocking) return;
    setError(""); setMsg("");
    setUnlocking(true);
    try {
      const r = await publicApi.walkinUnlock(pin);
      const names = r.data?.staff || [];
      const duty = r.data?.duty_today_name || "";
      setStaffList(names);
      setDutyToday(duty);
      // Pre-select rule: today's duty if present (and active); otherwise
      // first active member alphabetically (server already sorted).
      // Don't overwrite a user choice if they've already touched the field.
      setForm((f) => {
        if (f.served_by) return f;
        const preselect = (duty && names.includes(duty)) ? duty : (names[0] || "");
        return { ...f, served_by: preselect };
      });
      setUnlocked(true);
    } catch (e2) {
      const status = e2?.response?.status;
      setError(e2?.response?.data?.detail || "Something went wrong.");
      // Wrong PIN: clear the box so the next attempt starts clean.
      // Lockout (429) and not-configured (403) keep the message on screen.
      if (status === 401) setPin("");
    } finally { setUnlocking(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setMsg("");
    if (!form.served_by) {
      setError("Please select who served this walk-in.");
      return;
    }
    setLoading(true);
    try {
      const res = await publicApi.walkin({ pin, ...form });
      setMsg(res.data.message);
      setForm({
        customer_name: "", customer_phone: "", customer_email: "",
        watches_discussed: "", outcome_notes: "",
        // Keep the served_by selection between submissions — same person is
        // likely logging multiple consecutive walk-ins during a busy hour.
        served_by: form.served_by,
      });
    } catch (e2) {
      const status = e2?.response?.status;
      const detail = e2?.response?.data?.detail || "Something went wrong.";
      setError(detail);
      if (status === 401) { setUnlocked(false); setPin(""); }
    } finally { setLoading(false); }
  };

  // Subtle visual hint when the served_by selection diverges from today's
  // duty — not a blocker, just a "are you sure?" cue for the user.
  const divergesFromDuty = unlocked && form.served_by && dutyToday && form.served_by !== dutyToday;

  return (
    <PublicLayout>
      <div style={{ maxWidth: 520, margin: "0 auto", padding: "56px 28px 80px 28px" }}>
        <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Team only</p>
        <h1 style={{ fontSize: 32, margin: "0 0 28px 0" }}>Walk-in log</h1>

        {!unlocked ? (
          <form onSubmit={unlock} data-testid="walkin-pin-form">
            <label className="scw-label">Enter 4-digit PIN</label>
            <input
              className="scw-input"
              inputMode="numeric"
              maxLength={4}
              pattern="[0-9]*"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              data-testid="walkin-pin-input"
              autoFocus
            />
            <div style={{ marginTop: 24 }}>
              <SpinnerButton type="submit" loading={unlocking} disabled={pin.length !== 4} dataTestId="walkin-unlock-btn">Unlock</SpinnerButton>
            </div>
            {error && <p style={{ color: "#a13b3b", fontSize: 13, marginTop: 12 }} data-testid="walkin-pin-error">{error}</p>}
          </form>
        ) : (
          <form onSubmit={submit} data-testid="walkin-form">
            <div style={{ marginBottom: 22 }}>
              <label className="scw-label" htmlFor="walkin-served-by">Served by</label>
              <select
                id="walkin-served-by"
                className="scw-select"
                required
                value={form.served_by}
                onChange={(e) => setForm({ ...form, served_by: e.target.value })}
                data-testid="walkin-servedby-select"
                disabled={staffList.length === 0}
              >
                {staffList.length === 0 && <option value="">No team members</option>}
                {staffList.map((n) => (
                  <option key={n} value={n}>
                    {n}{n === dutyToday ? " (on duty today)" : ""}
                  </option>
                ))}
              </select>
              {divergesFromDuty && (
                <p style={{ marginTop: 6, fontSize: 12, color: "var(--scw-muted)", fontStyle: "italic" }} data-testid="walkin-servedby-hint">
                  On duty today is <strong>{dutyToday}</strong> {"\u2014"} logging under <strong>{form.served_by}</strong>.
                </p>
              )}
              {!dutyToday && (
                <p style={{ marginTop: 6, fontSize: 12, color: "var(--scw-muted)", fontStyle: "italic" }} data-testid="walkin-noduty-hint">
                  No one is on the duty rota today {"\u2014"} please pick who served the customer.
                </p>
              )}
            </div>
            <div style={{ marginBottom: 22 }}>
              <label className="scw-label">Customer name</label>
              <input className="scw-input" required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="walkin-name-input" />
            </div>
            <div style={{ marginBottom: 22 }}>
              <label className="scw-label">Customer phone</label>
              <input className="scw-input" required value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="walkin-phone-input" />
            </div>
            <div style={{ marginBottom: 22 }}>
              <label className="scw-label">Customer email (optional)</label>
              <input className="scw-input" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="walkin-email-input" />
            </div>
            <div style={{ marginBottom: 22 }}>
              <label className="scw-label">Watches discussed</label>
              <textarea className="scw-textarea" value={form.watches_discussed} onChange={(e) => setForm({ ...form, watches_discussed: e.target.value })} data-testid="walkin-watches-input" />
            </div>
            <div style={{ marginBottom: 22 }}>
              <label className="scw-label">Outcome notes</label>
              <textarea className="scw-textarea" value={form.outcome_notes} onChange={(e) => setForm({ ...form, outcome_notes: e.target.value })} data-testid="walkin-outcome-input" />
            </div>
            {error && <p style={{ color: "#a13b3b", fontSize: 13, marginBottom: 12 }} data-testid="walkin-error">{error}</p>}
            {msg && <p style={{ color: "#3a7d3a", fontSize: 13, marginBottom: 12 }} data-testid="walkin-success">{msg}</p>}
            <SpinnerButton loading={loading} dataTestId="walkin-submit-btn">Log walk-in</SpinnerButton>
          </form>
        )}
      </div>
    </PublicLayout>
  );
}
